import React from 'react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  cn,
} from '@roger/ui-components';

export const HOVER_CLOSE_DELAY_MS = 150;

type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
type PopoverAlign = 'start' | 'center' | 'end';

interface LongTextPreviewProps {
  text?: string | null;
  className?: string;
  contentClassName?: string;
  side?: PopoverSide;
  align?: PopoverAlign;
  placeholder?: React.ReactNode;
}

export function LongTextPreview({
  text,
  className,
  contentClassName = 'max-w-md',
  side = 'top',
  align = 'start',
  placeholder = '—',
}: LongTextPreviewProps) {
  const previewText = text?.trim() ?? '';
  const accessibleText = previewText.replace(/\s+/g, ' ');
  const [open, setOpen] = React.useState(false);
  const clickOpenedRef = React.useRef(false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const contentId = React.useId();
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearCloseTimeout = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openPreview = React.useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout]);

  const closePreviewSoon = React.useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      clickOpenedRef.current = false;
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        clearCloseTimeout();
        clickOpenedRef.current = false;
      }
    },
    [clearCloseTimeout]
  );

  const closePreviewIfNotPinned = React.useCallback(() => {
    // Focus state intentionally pins keyboard-opened previews.
    if (
      clickOpenedRef.current ||
      document.activeElement === triggerRef.current
    ) {
      return;
    }

    closePreviewSoon();
  }, [closePreviewSoon]);

  React.useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  if (!previewText) {
    return (
      <span className={cn('text-sm text-muted-foreground', className)}>
        {placeholder}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <button
          aria-describedby={open ? contentId : undefined}
          aria-label={accessibleText}
          ref={triggerRef}
          type='button'
          className={cn(
            'block max-w-full truncate rounded-sm text-left text-sm text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
          )}
          onBlur={() => {
            // Once focus leaves the trigger, pointer hover should own whether
            // the preview stays open; otherwise click-opened previews can pin.
            clickOpenedRef.current = false;
            closePreviewSoon();
          }}
          onClick={event => {
            event.stopPropagation();
            clickOpenedRef.current = true;
            openPreview();
          }}
          onFocus={openPreview}
          onPointerEnter={openPreview}
          onPointerLeave={closePreviewIfNotPinned}
        >
          {previewText}
        </button>
      </PopoverAnchor>
      <PopoverContent
        align={align}
        className={cn(
          'w-auto whitespace-pre-wrap break-words p-3 text-sm',
          contentClassName
        )}
        id={contentId}
        // Popover keeps one hover/click/focus implementation for this hybrid
        // preview; role='tooltip' matches the read-only aria-describedby usage.
        role='tooltip'
        // Keep focus anchored on the trigger for hover/focus previews; the
        // open content is exposed to assistive tech through aria-describedby.
        onCloseAutoFocus={event => event.preventDefault()}
        onOpenAutoFocus={event => event.preventDefault()}
        onPointerEnter={openPreview}
        onPointerLeave={closePreviewIfNotPinned}
        side={side}
      >
        {previewText}
      </PopoverContent>
    </Popover>
  );
}
